import React from 'react';
import { Spin } from 'antd';
import { useGetUserByIdQuery, useLoggedInStateQuery } from 'graphql/types';
import { Avatar, Button, Container } from 'components/elements';
import { dateFormat } from 'settings/configs';
import moment from 'moment';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { PageShell, PageHeader, PageTitle } from 'containers/groupsView/groupsView.styled';
import {
  MetaRow,
  MetaText,
  ProfileHeader,
  ProfileHeading,
  ProfileInfoWrap,
  ProfileName,
  ProfileTag,
  ProfileTop,
  ProfileWrapper,
  UserIdText,
} from 'containers/account/profile/profile.styled';
import {
  LoginNoticeCard,
  PublicProfileContent,
  ReferenceEmailItem,
  ReferenceEmailLabel,
  ReferenceEmailValue,
  ReferenceMetaRow,
} from './profile.styled';

type MatchParams = { id: string };
type PublicProfileProps = RouteComponentProps<MatchParams>;

const capitalize = (value?: string) => {
  if (!value) return '';
  return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
};

const PublicProfile = ({
  match: {
    params: { id },
  },
}: PublicProfileProps) => {
  const history = useHistory();
  const { data: loggedInData, loading: authLoading } = useLoggedInStateQuery();
  const isLoggedIn = Boolean(loggedInData?.loggedInState);

  const { data: profileResult, loading: profileLoading } = useGetUserByIdQuery({
    variables: { id },
    skip: !isLoggedIn,
  });

  const profile = profileResult?.usersPermissionsUser?.data?.attributes;
  const addressAttr = profileResult?.usersPermissionsUser?.data?.attributes?.address?.data?.attributes;
  const fullName = `${capitalize(profile?.firstname)} ${capitalize(profile?.lastname)}`.trim();
  const currentPlan =
    profile?.currentSubscription?.data?.attributes?.plan?.data?.attributes?.name;
  const location = [addressAttr?.city, addressAttr?.country].filter(Boolean).join(', ');

  const pageShell = (content: React.ReactNode) => (
    <Container>
      <PageShell>
        <PageHeader>
          <PageTitle>Profile</PageTitle>
        </PageHeader>
        <PublicProfileContent>{content}</PublicProfileContent>
      </PageShell>
    </Container>
  );

  if (authLoading) {
    return pageShell(
      <ProfileTop style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </ProfileTop>,
    );
  }

  if (!isLoggedIn) {
    return pageShell(
      <LoginNoticeCard>
        <p>You have to login to view this profile.</p>
        <Button type="primary" onClick={() => history.push('/login')}>
          Sign in
        </Button>
      </LoginNoticeCard>,
    );
  }

  if (profileLoading) {
    return pageShell(
      <ProfileTop style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </ProfileTop>,
    );
  }

  if (!profile) {
    return pageShell(
      <ProfileTop>
        <ProfileName>Profile not found</ProfileName>
      </ProfileTop>,
    );
  }

  return pageShell(
    <ProfileWrapper style={{ marginTop: 0 }}>
      <ProfileTop>
        <ProfileHeader>
          <Avatar avatarUrl={profile.avatarUrl} size="lg" />
          <ProfileInfoWrap>
            <ProfileHeading>
              <ProfileName>{fullName || 'Profile'}</ProfileName>
              {currentPlan ? <ProfileTag>{currentPlan}</ProfileTag> : null}
            </ProfileHeading>
            <UserIdText>Username: {profile.username || '—'}</UserIdText>
            <MetaRow>
              <MetaText>
                <i className="icon-pin" /> Location: {location || '—'}
              </MetaText>
              <MetaText>
                <i className="icon-birthday" /> Birthday:{' '}
                {profile.birthdate ? moment(profile.birthdate).format(dateFormat) : '—'}
              </MetaText>
              <MetaText>
                <i className="icon-calendar2" /> Joined:{' '}
                {profile.createdAt ? moment(profile.createdAt).format(dateFormat) : '—'}
              </MetaText>
            </MetaRow>
            <ReferenceMetaRow>
              <ReferenceEmailItem>
                <ReferenceEmailLabel>Reference email :</ReferenceEmailLabel>
                <ReferenceEmailValue>{profile.referenceUser || '—'}</ReferenceEmailValue>
              </ReferenceEmailItem>
            </ReferenceMetaRow>
          </ProfileInfoWrap>
        </ProfileHeader>
      </ProfileTop>
    </ProfileWrapper>,
  );
};

export default PublicProfile;
